import clsx from 'clsx';
import {
  FIELD_GROUPS,
  formatFieldValue,
  isTickable,
  type GridRow,
  type ProposalFieldSpec,
  type RowState,
} from './fieldSpec';

interface ComparisonGridProps {
  rows: GridRow[];
  /** Tick state by row key. Display-only rows have no entry. */
  ticks: Record<string, boolean>;
  onToggle: (key: string, checked: boolean) => void;
  /** Inline edits to the proposed value, by row key. */
  edits: Record<string, string>;
  onEdit: (key: string, value: string) => void;
  /** True when there is no WK brand to compare against (Create). */
  isCreate: boolean;
  /** The merged MA record, shown verbatim in the collapsed Raw payload section. */
  rawPayload: unknown;
  disabled?: boolean;
}

const STATE_HINT: Record<RowState, string> = {
  equal: 'Same on both sides — ticking only records the source',
  differ: 'WK already has a different value — tick to overwrite it',
  fill: 'WK is empty — safe to fill',
  new: 'New field on WK',
  display: 'Reference only — not written',
};

/** Media cells render the image itself; a URL alone says nothing about it. */
function ValueCell({
  spec,
  value,
  muted,
}: {
  spec: ProposalFieldSpec;
  value: unknown;
  muted?: boolean;
}) {
  const text = formatFieldValue(spec, value);
  if (!text) return <span className="text-gray-400">(empty)</span>;
  if (spec.kind === 'image') {
    return (
      <a href={text} target="_blank" rel="noreferrer" title={text}>
        <img
          src={text}
          alt={spec.label}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="h-12 max-w-[10rem] rounded border border-gray-200 bg-white object-contain"
        />
      </a>
    );
  }
  if (spec.kind === 'url') {
    return (
      <a
        href={text}
        target="_blank"
        rel="noreferrer"
        className={clsx('break-all underline', muted ? 'text-gray-500' : 'text-brand-700')}
      >
        {text}
      </a>
    );
  }
  return <span className={clsx('break-words', muted && 'text-gray-500')}>{text}</span>;
}

/**
 * Field-by-field comparison of an MA brand against a WK brand, with a tick per
 * writable field. Modelled on the review table in `BulkEntityAIEnhance`, minus
 * confidence scores and bulk mode.
 *
 * It owns no state: rows come from `buildGridRows`, ticks and edits from the
 * detail view, so the same data that draws the grid builds the seed.
 */
export function ComparisonGrid({
  rows,
  ticks,
  onToggle,
  edits,
  onEdit,
  isCreate,
  rawPayload,
  disabled = false,
}: ComparisonGridProps) {
  return (
    <div className="space-y-3">
      {FIELD_GROUPS.map((group) => {
        const groupRows = rows.filter((row) => row.spec.group === group.id);
        if (groupRows.length === 0) return null;
        return (
          <details key={group.id} open className="rounded-lg border border-gray-200 bg-white">
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-gray-800">
              {group.label}
              <span className="ml-2 font-normal text-gray-500">
                {groupRows.filter((row) => isTickable(row) && ticks[row.key]).length} of{' '}
                {groupRows.filter(isTickable).length} included
              </span>
            </summary>
            <table className="w-full table-fixed border-t border-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="w-[22%] px-3 py-1.5 font-medium">Field</th>
                  <th className="w-[32%] px-3 py-1.5 font-medium">WK current</th>
                  <th className="w-[36%] px-3 py-1.5 font-medium">MA proposed</th>
                  <th className="w-[10%] px-3 py-1.5 text-center font-medium">Include</th>
                </tr>
              </thead>
              <tbody>
                {groupRows.map((row) => {
                  const { spec } = row;
                  const writable = row.state !== 'display';
                  const locked = row.locked;
                  const checked = writable && !locked && Boolean(ticks[row.key]);
                  const inputId = `proposal-field-${row.key}`;
                  return (
                    <tr
                      key={row.key}
                      data-testid={`grid-row-${row.key}`}
                      data-state={row.state}
                      data-locked={locked ? 'true' : undefined}
                      title={locked ?? STATE_HINT[row.state]}
                      className={clsx(
                        'border-t border-gray-100 align-top',
                        row.state === 'equal' && 'bg-gray-50 text-gray-500',
                        row.state === 'differ' && 'bg-amber-50',
                        row.state === 'display' && 'text-gray-500',
                      )}
                    >
                      <td className="px-3 py-2">
                        <label htmlFor={writable ? inputId : undefined} className="font-medium text-gray-800">
                          {spec.label}
                        </label>
                        {spec.warning && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                            title={spec.warning}
                            data-testid={`grid-warning-${row.key}`}
                          >
                            ⚠ verify
                          </span>
                        )}
                        <div className="text-xs text-gray-400">{spec.maPath}</div>
                        {spec.warning && <p className="mt-1 text-xs text-amber-800">{spec.warning}</p>}
                      </td>
                      <td className="px-3 py-2">
                        {isCreate ? (
                          <span className="text-gray-400">— new brand —</span>
                        ) : row.state === 'new' ? (
                          <span className="italic text-gray-400">— new field —</span>
                        ) : spec.wkPath === null && row.wkValue === undefined ? (
                          <span className="italic text-gray-400">— no WK field —</span>
                        ) : (
                          <ValueCell spec={spec} value={row.wkValue} muted />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {spec.editable && writable ? (
                          <input
                            type="text"
                            aria-label={`${spec.label} (MA proposed)`}
                            value={edits[row.key] ?? formatFieldValue(spec, row.maValue)}
                            onChange={(e) => onEdit(row.key, e.target.value)}
                            disabled={disabled}
                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
                          />
                        ) : (
                          <ValueCell spec={spec} value={row.maValue} />
                        )}
                        {row.state === 'differ' && !locked && (
                          <div className="mt-1 text-xs font-medium text-amber-800">
                            Differs from WK — not included unless ticked
                          </div>
                        )}
                        {locked && (
                          <div
                            className="mt-1 text-xs font-medium text-gray-700"
                            data-testid={`grid-lock-${row.key}`}
                          >
                            <span aria-hidden="true">🔒</span> {locked}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {writable ? (
                          <input
                            id={inputId}
                            type="checkbox"
                            checked={checked}
                            disabled={disabled || Boolean(locked)}
                            onChange={(e) => onToggle(row.key, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                        ) : (
                          <span className="text-xs text-gray-400" title={STATE_HINT.display}>
                            display only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </details>
        );
      })}

      <details className="rounded-lg border border-gray-200 bg-white">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-gray-800">
          Raw payload
          <span className="ml-2 font-normal text-gray-500">everything MA returned, read-only</span>
        </summary>
        <pre className="max-h-96 overflow-auto border-t border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
          {JSON.stringify(rawPayload, null, 2)}
        </pre>
      </details>
    </div>
  );
}
